import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { api, formatDate, titleCase } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";

function Ratio({ worked, failed }) {
  const total = worked + failed;
  if (!total) return null;
  const pct = Math.round((worked / total) * 100);
  return (
    <div className="card p-5" data-testid="tag-ratio">
      <div className="flex justify-between text-sm mb-3">
        <span className="text-[#10B981] font-semibold">{worked} went-well notes</span>
        <span className="text-[#EF4444] font-semibold">{failed} struggle notes</span>
      </div>
      <div className="h-2 rounded-full bg-[#EF4444]/70 overflow-hidden">
        <div className="h-full bg-[#10B981] rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const Line = ({ color, children }) => (
  <li className="flex gap-2.5 text-sm text-[#E4E4E7] leading-snug">
    <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: color }} />
    {children}
  </li>
);

export default function TagPage() {
  const { name } = useParams();
  const { data, isLoading } = useQuery({ queryKey: ["tag", name], queryFn: () => api.get(`/tags/${encodeURIComponent(name)}`).then((r) => r.data) });

  return (
    <div>
      <PageHeader back eyebrow={data ? `${data.type} · ${data.count} session${data.count === 1 ? "" : "s"}` : " "} title={titleCase(decodeURIComponent(name))} />
      <div className="px-5 space-y-4">
        {isLoading && <div className="card h-40 animate-pulse" />}
        {data && <Ratio worked={data.worked_mentions} failed={data.failed_mentions} />}
        {data?.count === 0 && <div className="card p-6 text-sm text-[#A1A1AA]">This hasn't come up yet.</div>}
        <div className="eyebrow pt-2">Every time it came up</div>
        <div className="space-y-2.5" data-testid="tag-sessions">
          {data?.sessions.map((s, i) => {
            const n = data.notes[i];
            const any = (n.learned?.length || 0) + n.worked.length + n.failed.length + n.fix.length > 0;
            return (
              <Link key={s.id} to={`/session/${s.id}`} data-testid={`tag-session-${s.id}`} className="card p-4 block rise hover:border-[#3F3F46] transition-colors" style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-xs text-[#71717A]">{formatDate(s.local_date)}</div>
                    <div className="font-semibold">{s.title}</div>
                  </div>
                  <ChevronRight size={18} className="text-[#52525B]" />
                </div>
                {any ? (
                  <ul className="space-y-1.5 mt-2">
                    {n.learned?.map((t, j) => <Line key={`l${j}`} color="#A855F7">{t}</Line>)}
                    {n.worked.map((t, j) => <Line key={`w${j}`} color="#10B981">{t}</Line>)}
                    {n.failed.map((t, j) => <Line key={`f${j}`} color="#EF4444">{t}</Line>)}
                    {n.fix.map((t, j) => <Line key={`x${j}`} color="#F59E0B">{t}</Line>)}
                  </ul>
                ) : (
                  s.summary && <p className="text-sm text-[#A1A1AA]">{s.summary}</p>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
